import AppButton from '@nxsys/components/AppButton';
import { useDrawerSave } from './useDrawerSave';

import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppLoader from '@nxsys/components/AppLoader';
import AppSelect from '@nxsys/components/AppSelect';
import { holidayPaymentMethodOptions } from '@nxsys/constants/AppConstants';
import {
  HolidayMethodEnum,
  PAYMENT_MODEL_ENUM,
} from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useUpdateHolidayPayBalanceById,
  useUpdateSettingEmployeeById,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { Icons } from '@nxsys/types/icons';
import { showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import Inheritable from '@nxsys/components/Inheritable';
import { Col, Form, Modal, Row, Switch, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { round, toNumber } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import BalanceModal, { BalanceValues } from './components/BalanceModal';
import HistoryHolidayPayModal from './components/HistoryHolidayPayModal';
import AppPercentageInput from '@nxsys/components/AppPercentageInput';
import AppRateInput from '@nxsys/components/AppRateInput';

type SettingsHolidayPayProps = {
  readOnly?: boolean;
};

const SettingsHolidayPay = ({ readOnly }: SettingsHolidayPayProps = {}) => {
  const { messages } = useTypedIntl();
  const params = useParams();
  const navigate = useNavigate();
  const { payrollCountry, isEmployee, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const dispatch = useDispatch<AppDispatch>();
  const isIreland = payrollCountry === IRELAND;
  const employeeId = isEmployee
    ? user?.subcontractorId?.id
    : (params?.employeeId ?? null);

  const [form] = Form.useForm();
  const watchValues = Form.useWatch([], form);

  const isPayrollSettingLoading = useSelector(
    (state: AppState) => state.employeeStore.employeeSetting.loading,
  );
  const payrollSettingData = useSelector(
    (state: AppState) => state.employeeStore.employeeSetting.data,
  );
  const getPayrollSettingByEmployeeId = useCallback(
    (id: string) => dispatch(employeeActions.getPayrollSettingByEmployeeId(id)),
    [dispatch],
  );
  const getEmployeeById = useCallback(
    (id: string) => dispatch(employeeActions.getEmployeeById(id)),
    [dispatch],
  );

  const { run: updateSettingEmployeeById, loading: isUpdating } =
    useUpdateSettingEmployeeById();

  const { run: updateHolidayPayBalanceById } = useUpdateHolidayPayBalanceById();

  const filteredHolidayPayMethodOptions = useMemo(() => {
    const pm = payrollSettingData?.paymentModel;
    // 52-week averaging is a UK statutory holiday-pay reference period — no
    // Ireland equivalent, so it must not appear even for a PAYE employee.
    if (pm === PAYMENT_MODEL_ENUM.PAYEE && payrollCountry !== IRELAND) {
      return holidayPaymentMethodOptions;
    }
    return holidayPaymentMethodOptions.filter(
      (o) => o.value !== HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE,
    );
  }, [payrollSettingData?.paymentModel, payrollCountry]);

  const [openBalanceModal, setOpenBalanceModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);

  const handleUpdateBalance = async (values: BalanceValues) => {
    if (!employeeId) return;
    // Mirror umbrella-fe: send employer cost fields only for umbrella/hybrid,
    // otherwise send balance alone (BalanceModal already strips them).
    const payload: Record<string, number> = { balance: Number(values.balance) };
    if (values.appLevy !== undefined) payload.appLevy = Number(values.appLevy);
    if (values.ERNI !== undefined) payload.ERNI = Number(values.ERNI);
    if (values.ERPension !== undefined)
      payload.ERPension = Number(values.ERPension);
    await updateHolidayPayBalanceById(employeeId, payload as any);
    await getPayrollSettingByEmployeeId(employeeId);
    // Refresh the shared employee detail in redux so other tabs (e.g. P45
    // issuance in General Information) read the updated holiday pay balance.
    getEmployeeById(employeeId);
    setOpenBalanceModal(false);
    showMessage({ type: 'success', content: messages['common.updateSuccess'] });
  };

  useEffect(() => {
    if (employeeId) {
      getPayrollSettingByEmployeeId(employeeId);
    }
  }, [employeeId]);

  useEffect(() => {
    if (payrollSettingData) {
      form.setFieldsValue({
        holidayPayMethod:
          filteredHolidayPayMethodOptions.find(
            (item) => item.value === payrollSettingData?.holidayPayMethod,
          ) ?? null,
        holidayPayRate: payrollSettingData?.holidayPayRate ?? '',
        noOfWeeksWorked: payrollSettingData?.noOfWeeksWorked ?? '',
        includeInTimesheetRate:
          payrollSettingData?.includeInTimesheetRate ?? null,
        accureEmploymentCosts:
          (payrollSettingData as any)?.accrueEmploymentCosts ?? null,
        reclaimAppLevyCosts:
          (payrollSettingData as any)?.reclaimAppLevy ?? null,
      });
    }
  }, [payrollSettingData, filteredHolidayPayMethodOptions]);

  useEffect(() => {
    if (
      watchValues?.holidayPayMethod?.value ===
        HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE &&
      watchValues?.includeInTimesheetRate !== true
    ) {
      form.setFieldValue('includeInTimesheetRate', true);
    }
  }, [watchValues?.holidayPayMethod?.value]);

  const doSave = async (values: any) => {
    await updateSettingEmployeeById(employeeId!, {
      holidayPayMethod: values.holidayPayMethod?.value || null,
      holidayPayRate: values.holidayPayRate || null,
      noOfWeeksWorked:
        values.holidayPayMethod?.value ===
          HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE &&
        values.noOfWeeksWorked !== '' &&
        values.noOfWeeksWorked != null
          ? Number(values.noOfWeeksWorked)
          : null,
      includeInTimesheetRate:
        payrollSettingData?.paymentModel === PAYMENT_MODEL_ENUM.PAYEE
          ? (values.includeInTimesheetRate ?? null)
          : null,
      accrueEmploymentCosts: values.accureEmploymentCosts ?? null,
      reclaimAppLevy: values.reclaimAppLevyCosts ?? null,
    } as any);
    if (employeeId) {
      getPayrollSettingByEmployeeId(employeeId);
      getEmployeeById(employeeId);
    }
    showMessage({ type: 'success', content: messages['common.updateSuccess'] });
  };

  const handleSave = async () => {
    if (!employeeId) return;
    try {
      const values = await form.validateFields();

      const newMethod = values.holidayPayMethod?.value;
      const currentMethod = payrollSettingData?.holidayPayMethod;
      // NX-5493: read the backend round-once balance — this component renders
      // the same field in the Balance column, so re-deriving in float64 here
      // let the prompt and the column disagree by a penny at .xx5 ties.
      const hpBalance = Number(payrollSettingData?.holidayPayBalance || 0);
      // NX-5493: backend-computed in Setting.@AfterLoad — each ER component
      // rounded to its displayed 2dp figure, then summed. Raw components can
      // sum to £0.01 while every column on screen shows £0.00. `??` fallback
      // to the raw sum so a backend without the field fails closed (cosmetic
      // 1p prompt) instead of silently suppressing the warning.
      const erCosts =
        payrollSettingData?.retainedErCostsBalance ??
        Number((payrollSettingData?.retainedHolidayPayErCosts || 0).toFixed(2));
      const totalBalance = Number((hpBalance + erCosts).toFixed(2));

      // Match old UI: prompt to pay outstanding HP balance when switching to 52-week average
      if (
        newMethod === HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE &&
        currentMethod !== HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE &&
        totalBalance > 0
      ) {
        Modal.confirm({
          title: `There is a Holiday Balance of ${formatMoney(totalBalance, showCurrencyByCountry(payrollCountry))}`,
          content: 'Do you want to pay all of the holiday balance?',
          okText: 'Yes',
          cancelText: 'No',
          onOk: async () => {
            await doSave(values);
            navigate('/timesheets');
          },
          onCancel: () => {
            // No = do nothing, settings not saved
          },
        });
        return;
      }

      await doSave(values);
    } catch (error: any) {
      if (error?.errorFields) return;
    }
  };

  // NX-4682: in the quick-edit drawer, surface Save in the drawer header.
  const inDrawer = useDrawerSave(handleSave, isUpdating, !readOnly);

  if (!payrollSettingData || isPayrollSettingLoading)
    return (
      <div className="relative h-[100px]">
        <AppLoader />
      </div>
    );

  return (
    <div className="relative">
      {isUpdating && (
        <div className="absolute inset-0 z-10 bg-white/60">
          <AppLoader />
        </div>
      )}
      <Typography.Title
        level={5}
        className="!mb-8 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.holidayPay']}
      </Typography.Title>

      <AppFormContainer
        form={form}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        disabled={readOnly}
      >
        {/* HOLIDAY PAY SETTING */}
        <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider">
          {messages['common.holidayPaySetting'] ?? 'HOLIDAY PAY SETTING'}
        </Typography.Text>
        <div className="mt-4">
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={messages['common.holidayPayMethod']}
                name="holidayPayMethod"
              >
                <AppSelect
                  options={filteredHolidayPayMethodOptions}
                  className="w-full !min-w-0"
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={messages['common.holidayPayRate']}
                name="holidayPayRate"
              >
                <AppPercentageInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            {watchValues?.holidayPayMethod?.value ===
              HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE && (
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Number of Weeks Worked"
                  name="noOfWeeksWorked"
                >
                  <AppRateInput
                    placeholder={messages['form.enter']}
                    min={1}
                    max={52}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          {payrollSettingData?.paymentModel === PAYMENT_MODEL_ENUM.PAYEE && (
            <Form.Item name="includeInTimesheetRate">
              <Inheritable<boolean>
                label={
                  messages['common.includedTimesheetRate'] ??
                  'Included Timesheet Rate?'
                }
                name="includeInTimesheetRate"
                value={watchValues?.includeInTimesheetRate}
                inheritedValue={false}
                form={form}
                disabled={readOnly}
              >
                {({ value, onChange, disabled }) => (
                  <Switch
                    checked={value ?? false}
                    disabled={disabled}
                    onChange={onChange}
                  />
                )}
              </Inheritable>
            </Form.Item>
          )}

          {/* NX-5228: neither Irish calculation reads accrueEmploymentCosts or
              reclaimAppLevy, and Ireland has no Apprenticeship Levy at all.
              The company Payment Model form already hides both for Ireland. */}
          {!isBureau && !isIreland && (
            <Form.Item name="accureEmploymentCosts">
              <Inheritable<boolean>
                label={
                  messages['common.accureEmploymentCosts'] ??
                  'Accure Employment Costs'
                }
                name="accureEmploymentCosts"
                value={watchValues?.accureEmploymentCosts}
                inheritedValue={false}
                form={form}
                disabled={readOnly}
              >
                {({ value, onChange, disabled }) => (
                  <Switch
                    checked={value ?? false}
                    disabled={disabled}
                    onChange={onChange}
                  />
                )}
              </Inheritable>
            </Form.Item>
          )}
          {!isBureau && !isIreland && (
            <Form.Item name="reclaimAppLevyCosts">
              <Inheritable<boolean>
                label={
                  messages['common.reclaimAppLevyCosts'] ??
                  'Reclaim App Levy Costs'
                }
                name="reclaimAppLevyCosts"
                value={watchValues?.reclaimAppLevyCosts}
                inheritedValue={false}
                form={form}
                disabled={readOnly}
              >
                {({ value, onChange, disabled }) => (
                  <Switch
                    checked={value ?? false}
                    disabled={disabled}
                    onChange={onChange}
                  />
                )}
              </Inheritable>
            </Form.Item>
          )}
        </div>

        {/* Divider */}
        <div className="h-[8px] w-full rounded-[4px] bg-[#EFEFEF] my-6" />

        {/* HOLIDAY PAY BALANCE */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider">
              {messages['common.holidayPayBalance'] ?? 'HOLIDAY PAY BALANCE'}
            </Typography.Text>
            <div className="flex items-center gap-2">
              <AppButton
                variant="outlined"
                name={messages['common.openingBalance'] ?? 'Opening Balance'}
                size="small"
                onClick={() => setOpenBalanceModal(true)}
              />
              <div
                className="flex items-center gap-1 cursor-pointer bg-[#F1F1F1] rounded-[4px] h-[24px] px-[6px]"
                onClick={() => setOpenHistoryModal(true)}
              >
                <Icons.IconTimePast width={14} height={14} color="#444444" />
                <span className="text-[12px] leading-[1.67] text-[#000000]">
                  {messages['common.history'] ?? 'History'}
                </span>
              </div>
            </div>
          </div>

          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item label={messages['common.openingBalance']}>
                <AppInput
                  disabled
                  value={
                    payrollSettingData?.holidayPayOpeningBalance != null
                      ? formatMoney(
                          payrollSettingData.holidayPayOpeningBalance,
                          showCurrencyByCountry(payrollCountry),
                        )
                      : ''
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={messages['common.accrued']}>
                <AppInput
                  disabled
                  value={
                    payrollSettingData?.holidayPaySumOfAccrued != null
                      ? formatMoney(
                          payrollSettingData.holidayPaySumOfAccrued,
                          showCurrencyByCountry(payrollCountry),
                        )
                      : ''
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item label={messages['common.paid']}>
                <AppInput
                  disabled
                  value={
                    payrollSettingData?.holidayPaySumOfPaid != null
                      ? formatMoney(
                          payrollSettingData.holidayPaySumOfPaid,
                          showCurrencyByCountry(payrollCountry),
                        )
                      : ''
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={messages['common.balance'] ?? 'Balance'}>
                <AppInput
                  disabled
                  value={
                    payrollSettingData?.holidayPayBalance != null
                      ? formatMoney(
                          payrollSettingData.holidayPayBalance,
                          showCurrencyByCountry(payrollCountry),
                        )
                      : ''
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          {(payrollSettingData?.paymentModel === PAYMENT_MODEL_ENUM.UMBRELLA ||
            payrollSettingData?.paymentModel === PAYMENT_MODEL_ENUM.HYBRID) && (
            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Form.Item label={messages['common.appLevy']}>
                  <AppInput
                    disabled
                    value={
                      payrollSettingData?.retainedHolidayPayAppLevy != null
                        ? formatMoney(
                            payrollSettingData.retainedHolidayPayAppLevy,
                            showCurrencyByCountry(payrollCountry),
                          )
                        : ''
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label={messages['common.ERNI']}>
                  <AppInput
                    disabled
                    value={
                      payrollSettingData?.retainedHolidayPayERNI != null
                        ? formatMoney(
                            payrollSettingData.retainedHolidayPayERNI,
                            showCurrencyByCountry(payrollCountry),
                          )
                        : ''
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label={messages['common.employerPension']}>
                  <AppInput
                    disabled
                    value={
                      payrollSettingData?.retainedHolidayPayErPen != null
                        ? formatMoney(
                            payrollSettingData.retainedHolidayPayErPen,
                            showCurrencyByCountry(payrollCountry),
                          )
                        : ''
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
        </div>
      </AppFormContainer>

      {/* Action Buttons */}
      {!readOnly && !inDrawer && (
        <div className="flex items-center justify-start gap-3 pt-6">
          <AppButton
            type="primary"
            name={messages['common.save']}
            leftIcon={
              <Icons.IconCheckCircle color={defaultTheme.theme.palette.white} />
            }
            onClick={handleSave}
            loading={isUpdating}
          />
        </div>
      )}

      <BalanceModal
        isOpen={openBalanceModal}
        toggle={() => setOpenBalanceModal(false)}
        onSubmit={handleUpdateBalance}
        paymentModel={payrollSettingData?.paymentModel ?? undefined}
        defaultValues={{
          balance: payrollSettingData?.holidayPayBalance
            ? round(toNumber(payrollSettingData.holidayPayBalance), 2)
            : undefined,
          // Prefill retained employer costs (umbrella/hybrid only) —
          // matches umbrella-fe ModalUpdateHoliday initial values.
          appLevy: round(
            toNumber(payrollSettingData.retainedHolidayPayAppLevy ?? 0),
            2,
          ),
          ERNI: round(
            toNumber(payrollSettingData.retainedHolidayPayERNI ?? 0),
            2,
          ),
          ERPension: round(
            toNumber(payrollSettingData.retainedHolidayPayErPen ?? 0),
            2,
          ),
        }}
      />
      <HistoryHolidayPayModal
        isOpen={openHistoryModal}
        toggle={() => setOpenHistoryModal(false)}
        employeeId={employeeId!}
        paymentModel={payrollSettingData?.paymentModel ?? undefined}
      />
    </div>
  );
};

export default SettingsHolidayPay;
